import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDeferredValue, useState } from 'react';

import { pickerQuery } from './sources';

import type { BrowseCollectionsResponse } from '@platform/client';

type BrowsedModule = BrowseCollectionsResponse['collections'][number];

/**
 * A module, typed into as well as browsed — ADMIN-16, and PATH-02's pickers.
 *
 * The search runs on the server, so a module past the picker's first hundred is found.
 * Each option names its course, because two courses can each have a module called
 * "Foundations".
 */
export function ModulePicker({
  width,
  onChange,
  label = 'Module',
  hide,
  detailed = false,
  initialSlug,
}: {
  width: number;
  onChange: (module: BrowsedModule | null) => void;
  label?: string;
  /** Ids not to offer — the modules a path already holds. */
  hide?: ReadonlySet<string>;
  /** Status and lesson count beside the course, for choosing what to add. */
  detailed?: boolean;
  /** The module chosen before a reload, by slug — the Paths list keeps it in the URL. */
  initialSlug?: string;
}) {
  const [chosen, setChosen] = useState<BrowsedModule | null>(null);
  const [typed, setTyped] = useState<string>();

  const restoring = chosen === null && typed === undefined && initialSlug;
  const restored = useQuery({
    ...pickerQuery('MODULE', initialSlug ?? ''),
    enabled: Boolean(restoring),
  });
  const shown =
    chosen ??
    (restoring
      ? (restored.data?.data.collections.find(
          (one) => one.slug === initialSlug,
        ) ?? null)
      : null);
  const input = typed ?? shown?.title ?? '';

  // The input shows the chosen title, so searching for it would hide every other module
  // the moment the list reopens.
  const q = useDeferredValue(
    shown !== null && input === shown.title ? '' : input.trim(),
  );
  const found = useQuery({
    ...pickerQuery('MODULE', q),
    placeholderData: keepPreviousData,
  });
  const options = (found.data?.data.collections ?? []).filter(
    (one) => !hide?.has(one.id),
  );

  return (
    <Autocomplete
      size="small"
      options={options}
      value={shown}
      inputValue={input}
      loading={found.isFetching}
      filterOptions={(all) => all}
      getOptionLabel={(one) => one.title}
      isOptionEqualToValue={(one, other) => one.id === other.id}
      noOptionsText={
        q !== ''
          ? `No module matches “${q}”.`
          : hide === undefined
            ? 'No modules yet.'
            : 'No modules to add.'
      }
      onInputChange={(_event, next) => {
        setTyped(next);
      }}
      onChange={(_event, next) => {
        setChosen(next);
        onChange(next);
      }}
      // MUI keys an option by its label, and two courses can each have a "Foundations":
      // the shared key left a stale row in the list after the next search.
      renderOption={({ key: _byLabel, ...props }, one) => (
        <li key={one.id} {...props}>
          <span>
            {one.title}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              {[
                one.parentTitle,
                ...(detailed
                  ? [one.status, `${String(one.lessons)} lesson(s)`]
                  : []),
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </span>
        </li>
      )}
      renderInput={(params) => <TextField {...params} label={label} />}
      sx={{ minWidth: width }}
    />
  );
}
